import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AuthenticationType,
  Dynamics365Connection,
  Dynamics365ConnectionHandlerService,
  GrantType,
  ManagedIdentityAuthentication,
  ManagedIdentityType,
  ModernAuthentication,
  PasswordAuthentication
} from "@coso/connection-forms";
import { FormText, ViewMode, FormBuilder, FormGroup } from "@coso/forms";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { forkJoin, Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { AUserPermissionsService } from "../../../services/a-user-permissions.service";
import { ConnectionMapperService } from "../../../services/connection-mapper.service";
import { Dynamics365ConnectionService } from "../../../services/dynamics-365-connection.service";
import { EnvironmentService } from "../../../services/environment.service";
import { ReplicationService } from "../../../services/replication.service";
import { SettingsService } from "../../../services/settings.service";
import { handleError } from "../../../utils/handle-error";
import { isRunning } from "../../../utils/is-running";
import { SettingsValidator } from "../../../utils/settings-validator";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { IS_DOCKER } from "@coso-replicator/core";
import { Validators } from "@angular/forms";

@Component({
  standalone:false,
  templateUrl: "./dynamics365.component.html",
  styleUrls: ["./dynamics365.component.scss"]
})
export class Dynamics365Component implements OnInit, OnDestroy {
  private unsubscribe = new Subject<void>();
  @ViewChild("organizationUrlPopover", { static: true }) organizationUrlPopover: TemplateRef<any>;
  @ViewChild("validationRef", { static: true }) validationRef: TemplateRef<any>;
  incompatibilities: string[];

  instanceId: string;
  get title() {
    if (this.isConfigured) {
      if (this.isReadOnly) {
        return "View Dynamics 365 connection";
      } else {
        return "Edit Dynamics 365 connection";
      }
    } else {
      return "Create Dynamics 365 connection";
    }
  }
  isConfigured = false;
  isReadOnly: boolean;
  isRunning: boolean;
  ViewMode = ViewMode;
  errorMessage: string = undefined;
  formGroup: FormGroup<Dynamics365Connection>;
  formText: FormText<Dynamics365Connection>;
  private original: Dynamics365Connection;

  constructor(
    @Inject(IS_DOCKER) public isDocker: boolean,
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private fb: FormBuilder,
    public dynamics365ConnectionHandler: Dynamics365ConnectionHandlerService,
    private router: Router,
    private route: ActivatedRoute,
    private dynamics365ConnectionService: Dynamics365ConnectionService,
    private connectionMapperService: ConnectionMapperService,
    private userPermissionsService: AUserPermissionsService,
    private replicationService: ReplicationService,
    private modalService: NgbModal,
    private settingsService: SettingsService,
    private environmentService: EnvironmentService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
    sessionStorage.removeItem("instance-id");
  }

  ngOnInit() {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    sessionStorage.setItem("instance-id", this.instanceId);
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.userPermissionsService.isCustomerAgent$]).subscribe(
      ([status, hasLimitedAccess, isCustomerAgent]) => {
        this.isRunning = isRunning(status);
        this.isReadOnly = hasLimitedAccess || isCustomerAgent || isRunning(status);
      },
      (error) => (this.errorMessage = `Something went wrong due to: ${handleError(error)}`)
    );
    this.generateFormGroup();
    this.loadConnection();
  }

  private generateFormGroup() {
    this.formGroup = this.fb.group<Dynamics365Connection>({
      id: this.fb.control(null),
      organizationUrl: this.fb.control(null),
      authenticationType: this.fb.control(AuthenticationType.Modern),
      modernAuthentication: this.fb.group<ModernAuthentication>({
        tenantId: this.fb.control(null),
        azureEndpoint: this.fb.control("https://login.microsoftonline.com/"),
        clientId: this.fb.control(null),
        certificateThumbprint: this.fb.control(null),
        useDefaultCertificate: this.fb.control(true, [Validators.required]),
        grantType: this.fb.control(GrantType.ClientCredentials),
        clientSecret: this.fb.control(null),
        scope: this.fb.control("offline_access"),
        encryptedToken: this.fb.control(null),
        changeAzureAdApp: this.fb.control(false),
        useDefaultAzureAdApp: this.fb.control(true),
        // The internal scope for which the grant is considered valid for SaaS services (sent on state parameter for SaaS ConsentCallback management service).
        saasConsentCallbackGrantScope: this.fb.control(this.instanceId),
        apiPermission: this.fb.control(null)
      }),
      passwordAuthentication: this.fb.group<PasswordAuthentication>({
        username: this.fb.control(null),
        password: this.fb.control(null),
        changePassword: this.fb.control(false)
      }),
      managedIdentityAuthentication: this.fb.group<ManagedIdentityAuthentication>({
        type: this.fb.control(ManagedIdentityType.SystemAssigned),
        clientId: this.fb.control(null)
      })
    });
    this.formGroup.controls.organizationUrl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter((u) => !!u)
      )
      .subscribe((value) => {
        if (this.formGroup.controls.organizationUrl.valid) {
          const scope = `offline_access ${value.replace(/\/$/, "")}/user_impersonation`;
          this.formGroup.controls.modernAuthentication.controls.scope.setValue(scope);
        }
      });
    this.formText = {
      organizationUrl: { popover: this.organizationUrlPopover }
    };
  }

  saveConnection() {
    if (this.isReadOnly) {
      return;
    }
    this.dynamics365ConnectionHandler
      .testConnectionResultFor$(this.formGroup.value.internalId)
      .pipe(
        filter((r) => !!r && r.isSuccess),
        take(1)
      )
      .subscribe((r) => {
        this.formGroup.controls.id.setValue(r.payload);
        if (this.isConfigured && this.formGroup.value.organizationUrl !== this.original.organizationUrl) {
          this.loadingService.start("Validating current configuration", "validateOnOrganizationUrlChange");
          this.settingsService.get(this.instanceId).subscribe(
            (data) => {
              const validationResult = SettingsValidator.validateOnOrganizationUrlChange(data);
              if (validationResult.valid) {
                this.save();
              } else {
                this.incompatibilities = validationResult.incompatibilities;
                /** Prompt user about incompatibilities with the current settings. */
                this.errorMessage = undefined;
                const modalRef = this.modalService.open(DialogComponent, {
                  keyboard: false,
                  ariaLabelledBy: "modalTitle"
                });
                modalRef.componentInstance.options = {
                  titleText: "Incompatible custom settings",
                  type: DialogType.Warning,
                  contentTemplateRef: this.validationRef,
                  rightButtons: [
                    {
                      label: "No",
                      type: "button",
                      class: "btn btn-dark mr-2",
                      onClick: () => {
                        this.errorMessage = `The operation was cancelled.`;
                        modalRef.dismiss();
                      }
                    },
                    {
                      label: "Yes",
                      type: "button",
                      class: "btn btn-primary",
                      onClick: () => {
                        const settings = {
                          ...data,
                          sharePoint: {
                            ...data.sharePoint,
                            sharepointConfig: data.sharePoint.sharepointConfig.map((config) => {
                              return {
                                ...config,
                                userMapping: { ...config.userMapping, userPairs: [] },
                                permissionMapping: { ...config.permissionMapping, permissionPairs: [] },
                                specialPermissions: []
                              };
                            })
                          }
                        };
                        this.settingsService.save(this.instanceId, settings).subscribe(
                          () => {
                            this.save();
                            modalRef.dismiss();
                          },
                          (error) => {
                            this.errorMessage = `Failed to save settings due to: ${handleError(error)}`;
                            modalRef.dismiss();
                          }
                        );
                      }
                    }
                  ],
                  onClose: () => modalRef.dismiss()
                };
              }
              this.loadingService.stop("validateOnOrganizationUrlChange");
            },
            (error) => {
              this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
              this.loadingService.stop("validateOnOrganizationUrlChange");
            }
          );
        } else {
          this.save();
        }
      });
    this.dynamics365ConnectionHandler
      .isTestConnectionSuccessfulFor$(this.formGroup.value.internalId)
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) {
          this.dynamics365ConnectionHandler.testConnection(this.formGroup.getRawValue());
        }
      });
  }

  returnToList(message?: string) {
    this.router.navigate(["../"], {
      relativeTo: this.route,
      state: { message }
    });
  }

  private save() {
    this.errorMessage = undefined;
    this.loadingService.start("Saving Dynamics 365 connection");
    const connection = this.connectionMapperService.mapFromDynamics365Connection(this.formGroup.getRawValue());
    this.dynamics365ConnectionService.upsertConnection(this.instanceId, connection).subscribe(
      () => {
        this.returnToList("Dynamics 365 connection was successfully saved.");
        this.loadingService.stop();
      },
      (error) => {
        this.errorMessage = `Failed to save Dynamics 365 connection due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private loadConnection() {
    this.loadingService.start("Loading Dynamics 365 connection", "loadD365Connection");
    forkJoin([this.environmentService.getDefaultClientId(), this.dynamics365ConnectionService.getConnection(this.instanceId)]).subscribe(
      ([defaultClientId, data]) => {
        const connection = this.connectionMapperService.mapToDynamics365Connection(data);
        this.original = connection;
        this.isConfigured = !!data.server;
        this.formGroup.patchValue(connection);

        this.formGroup.controls.modernAuthentication.controls.grantType.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((grantType) => {
          if (this.original.modernAuthentication.grantType == GrantType.ClientCredentials && grantType != GrantType.ClientCredentials) {
            this.formGroup.controls.modernAuthentication.controls.changeAzureAdApp.setValidators([Validators.required]);
          }
          this.formGroup.controls.modernAuthentication.controls.changeAzureAdApp.updateValueAndValidity();
        });

        if (defaultClientId && !connection.modernAuthentication.clientId) {
          this.formGroup.controls.modernAuthentication.controls.clientId.setValue(defaultClientId);
        }
        if (connection.authenticationType === AuthenticationType.ManagedIdentity && connection.managedIdentityAuthentication.clientId) {
          this.formGroup.controls.managedIdentityAuthentication.controls.type.setValue(ManagedIdentityType.UserAssigned);
          this.formGroup.controls.managedIdentityAuthentication.controls.clientId.setValue(connection.managedIdentityAuthentication.clientId);
        }
        this.loadingService.stop("loadD365Connection");
      },
      (error) => {
        this.errorMessage = `Failed to get Dynamics 365 connection due to: ${handleError(error)}`;
        this.loadingService.stop("loadD365Connection");
      }
    );
  }
}
